<template>
	<div ref="root" class="map-controls" :style="{ '--right-offset': rightOffset }">
		<div class="control">
			<button
				type="button"
				class="trigger"
				:class="{ active: openPanel === 'tools' }"
				:title="t('layers.map-tools.title')"
				@click="toggle('tools')"
			>
				<AppIcon :name="IconName.Ruler" />
			</button>
			<div v-if="openPanel === 'tools'" class="panel tools" data-tour-related="map-controls">
				<button type="button" class="icon-btn" :title="t('layers.map-tools.distance')" @click="measure('distance')">
					<AppIcon :name="IconName.LineSegment" />
				</button>
				<button type="button" class="icon-btn" :title="t('layers.map-tools.area')" @click="measure('area')">
					<AppIcon :name="IconName.Pentagon" />
				</button>
				<div class="separator" />
				<button type="button" class="icon-btn danger" :title="t('layers.map-tools.clear')" @click="clear">
					<AppIcon :name="IconName.Trash" />
				</button>
			</div>
		</div>

		<div class="control">
			<button
				type="button"
				class="trigger"
				:class="{ active: openPanel === 'base-map' }"
				:title="t('layers.base-map.title')"
				@click="toggle('base-map')"
			>
				<AppIcon :name="IconName.Layers" />
			</button>
			<div v-if="openPanel === 'base-map'" class="panel base-map" data-tour-related="map-controls">
				<div class="heading">{{ t("layers.base-map.title") }}</div>
				<div v-for="group in groups" :key="group.label" class="group">
					<div class="group-label">{{ group.label }}</div>
					<button
						v-for="style in group.styles"
						:key="style.value"
						type="button"
						class="row"
						:class="{ selected: style.value === currentStyle }"
						@click="selectStyle(style.value)"
					>
						<span>{{ style.label }}</span>
						<span v-if="style.value === currentStyle" class="dot" />
					</button>
				</div>
			</div>
		</div>

		<div class="control">
			<button
				type="button"
				class="trigger rotate"
				:class="{ active: rotating }"
				:title="t('layers.map-tools.reset-north')"
				@pointerdown="startRotate"
			>
				<AppIcon :name="IconName.CompassNeedle" :style="{ transform: `rotate(${-bearing}deg)` }" />
			</button>
		</div>

		<div class="control">
			<button
				type="button"
				class="trigger"
				:class="{ active: attributionOpen }"
				:title="t('layers.map-tools.attribution')"
				@click="toggleAttribution"
			>
				<AppIcon :name="IconName.Info" />
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { onClickOutside } from "@vueuse/core"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { MAP_STYLES, type MapStyleValue } from "@/components/constants"
import { IconName } from "@/components/icons/registry"
import { analytics } from "@/utils/analytics"

type MeasureKind = "distance" | "area"
type PanelName = "tools" | "base-map"

const props = withDefaults(
	defineProps<{
		currentStyle: MapStyleValue
		bearing?: number
		pitch?: number
		measuring?: boolean
		rightOffset?: string
	}>(),
	{ bearing: 0, pitch: 0, measuring: false, rightOffset: "0px" }
)

const emit = defineEmits<{
	"update:currentStyle": [MapStyleValue]
	measure: [MeasureKind]
	"clear-measures": []
	"reset-north": []
	rotate: [{ bearing: number; pitch: number }]
	"set-attribution": [boolean]
}>()

const { t } = useI18n()

const openPanel = ref<PanelName | null>(null)
const attributionOpen = ref(false)
const rotating = ref(false)
const root = ref<HTMLElement | null>(null)

const DRAG_THRESHOLD_PX = 3
const BEARING_SENSITIVITY = 0.5
const PITCH_SENSITIVITY = 0.5
const MAX_PITCH_DEGREES = 85

function startRotate(event: PointerEvent): void {
	event.preventDefault()
	const origin = { x: event.clientX, y: event.clientY, bearing: props.bearing, pitch: props.pitch }
	let dragged = false

	function onMove(move: PointerEvent): void {
		const deltaX = move.clientX - origin.x
		const deltaY = move.clientY - origin.y
		if (!dragged && Math.abs(deltaX) + Math.abs(deltaY) > DRAG_THRESHOLD_PX) {
			dragged = true
			rotating.value = true
		}
		if (!dragged) return
		emit("rotate", {
			bearing: origin.bearing - deltaX * BEARING_SENSITIVITY,
			pitch: Math.min(MAX_PITCH_DEGREES, Math.max(0, origin.pitch - deltaY * PITCH_SENSITIVITY)),
		})
	}

	function onUp(): void {
		window.removeEventListener("pointermove", onMove)
		window.removeEventListener("pointerup", onUp)
		rotating.value = false
		if (!dragged) emit("reset-north")
	}

	window.addEventListener("pointermove", onMove)
	window.addEventListener("pointerup", onUp)
}

onClickOutside(
	root,
	() => {
		if (!(openPanel.value === "tools" && props.measuring)) {
			openPanel.value = null
		}
		setAttribution(false)
	},
	{ ignore: [".mapboxgl-ctrl-attrib"] }
)

function setAttribution(open: boolean): void {
	if (attributionOpen.value === open) return
	attributionOpen.value = open
	emit("set-attribution", open)
}

function toggleAttribution(): void {
	openPanel.value = null
	setAttribution(!attributionOpen.value)
}

const groups = computed(() => [
	{
		label: t("layers.base-map.groups.mapbox"),
		styles: [
			{ value: MAP_STYLES.SIMPLE, label: t("layers.base-map.styles.simple") },
			{ value: MAP_STYLES.STANDARD, label: t("layers.base-map.styles.standard") },
			{ value: MAP_STYLES.SATELLITE, label: t("layers.base-map.styles.satellite") },
		],
	},
	{
		label: t("layers.base-map.groups.external"),
		styles: [
			{ value: MAP_STYLES.GOOGLE, label: t("layers.base-map.styles.google") },
			{ value: MAP_STYLES.MERN, label: t("layers.base-map.styles.mern") },
		],
	},
])

function toggle(panel: PanelName): void {
	setAttribution(false)
	openPanel.value = openPanel.value === panel ? null : panel
}

function selectStyle(value: MapStyleValue): void {
	if (value !== props.currentStyle) {
		analytics.baseMapChanged(value)
		emit("update:currentStyle", value)
	}
	openPanel.value = null
}

function measure(kind: MeasureKind): void {
	emit("measure", kind)
}

function clear(): void {
	emit("clear-measures")
}
</script>

<style scoped>
.map-controls {
	position: fixed;
	right: calc(var(--spacing-md) + var(--right-offset, 0px));
	bottom: var(--spacing-md);
	z-index: 10;
	display: flex;
	flex-direction: column;
	font-family: var(--font-family-primary);
	transition: right 0.2s ease;
}

.control {
	position: relative;
}

.control:not(:first-child) .trigger {
	margin-top: calc(-1 * var(--border-width-thin));
	border-top-left-radius: 0;
	border-top-right-radius: 0;
}

.control:not(:last-child) .trigger {
	border-bottom-left-radius: 0;
	border-bottom-right-radius: 0;
}

.trigger {
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--size-button);
	height: var(--size-button);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-0);
	color: var(--neutral-700);
	box-shadow: var(--shadow-md);
	cursor: pointer;

	&:hover {
		color: var(--neutral-900);
	}

	&.active {
		position: relative;
		z-index: 1;
		border-color: var(--primary-green);
		color: var(--primary-green);
	}

	&.rotate {
		cursor: grab;

		&.active {
			cursor: grabbing;
		}
	}
}

.panel {
	position: absolute;
	right: 0;
	bottom: calc(100% + var(--spacing-sm));
	min-width: 0;
	width: max-content;
	padding: var(--spacing-sm);
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	box-shadow: var(--shadow-lg);

	.heading {
		padding: var(--spacing-xs) var(--spacing-sm);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-semibold);
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--neutral-700);
	}

	.group {
		margin-top: var(--spacing-xs);
		padding-top: var(--spacing-xs);
		border-top: var(--border-width-thin) solid var(--neutral-200);
	}

	.group-label {
		padding: var(--spacing-xs) var(--spacing-sm);
		font-size: 9px;
		font-weight: var(--font-weight-semibold);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--neutral-400);
	}

	.row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-sm);
		width: 100%;
		padding: var(--spacing-sm);
		border: none;
		border-radius: var(--border-radius-md);
		background: transparent;
		font-family: inherit;
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
		color: var(--neutral-900);
		text-align: left;
		cursor: pointer;

		&:hover {
			background: var(--neutral-50);
		}

		&.selected {
			color: var(--primary-green);
		}
	}

	.dot {
		width: var(--width-line-dot);
		height: var(--height-line-dot);
		border-radius: var(--border-radius-full);
		background: var(--primary-green);
	}
}

.panel.base-map {
	bottom: calc(100% + var(--size-button) + var(--spacing-sm));
}

.panel.tools {
	display: flex;
	align-items: center;
	gap: var(--spacing-xxs);
	min-width: auto;
	padding: var(--spacing-xs);

	.separator {
		width: var(--border-width-thin);
		height: var(--size-icon-lg);
		margin: 0 var(--spacing-xs);
		background: var(--neutral-200);
	}
}

.icon-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: var(--size-button-s);
	height: var(--size-button-s);
	border: none;
	border-radius: var(--border-radius-md);
	background: transparent;
	color: var(--neutral-800);
	cursor: pointer;

	&:hover {
		background: var(--neutral-50);
	}

	&.danger {
		color: var(--accent-warning);
	}
}
</style>
